import React, { Component } from 'react';
import track, { TrackingPropType } from 'react-tracking';
import { func, number, shape, string, object } from 'prop-types';
import { withAnalytics, safeBuildEventObj } from '@ynap/analytics';
import BEMHelper from '@ynap/bem-helper';
import { performance } from '@ynap/clientside-monitoring';
import { withOptimizely } from '@ynap/optimizely-utils';
import { Link } from '@ynap/router';
import { trackPagination } from './analytics';

const bem = new BEMHelper('Pagination');

@track()
export class Pagination extends Component {
    constructor() {
        super();

        this.onPaginateHandler = this.onPaginateHandler.bind(this);
    }

    @track(({ analytics }, state, [value]) => safeBuildEventObj(trackPagination, analytics, value))
    onPaginateHandler(buttonType) {
        const { optimizely } = this.props;

        const optimizelyEventsMap = {
            previous: 'view_previous_plp',
            next: 'view_more_plp',
        };

        if (optimizely) optimizely.track(optimizelyEventsMap[buttonType]);

        performance.intTiming.markSpaInteractionTime('pagination');
    }

    render() {
        const { className, currentPage, customPageSize, generateListingUrl, messages, totalPages } = this.props;

        const previousPage = currentPage > 1 ? currentPage - 1 : 1;
        const nextPage = currentPage < totalPages ? currentPage + 1 : totalPages;

        const previousPageParams = previousPage > 1 ? { replaceParams: { pageNumber: previousPage } } : { clearParamsByKey: ['pageNumber'] };

        const firstPageLink = generateListingUrl({ clearParamsByKey: ['pageNumber'] });
        const lastPageLink = generateListingUrl({ replaceParams: { pageNumber: totalPages, pageSize: customPageSize } });
        const previousPageLink = generateListingUrl(previousPageParams);
        const nextPageLink = generateListingUrl({ replaceParams: { pageNumber: nextPage, pageSize: customPageSize } });
        const disabledFirstPage = currentPage === 1 ? 'disabled' : null;
        const disabledLastPage = currentPage === totalPages ? 'disabled' : null;
        const singlePage = totalPages < 2;

        return (
            <div className={bem(null, null, className)}>
                <div className={bem('wrapper')}>
                    {!singlePage && (
                        <div className={bem('box')}>
                            <Link
                                aria-disabled={disabledFirstPage ? 'true' : 'false'}
                                aria-label={disabledFirstPage ? messages?.pagination?.a11yUnavailable?.() : messages?.pagination?.a11yFirst?.()}
                                className={bem('first', disabledFirstPage, className)}
                                onClick={() => this.onPaginateHandler('first')}
                                tabIndex={disabledFirstPage ? '-1' : '0'}
                                to={firstPageLink}
                            >
                                <div className={bem('firstIcon', disabledFirstPage, className)} />
                            </Link>
                            <Link
                                aria-disabled={disabledFirstPage ? 'true' : 'false'}
                                className={bem('previous', disabledFirstPage, className)}
                                onClick={() => this.onPaginateHandler('previous')}
                                tabIndex={disabledFirstPage ? '-1' : '0'}
                                to={previousPageLink}
                                aria-label={
                                    disabledFirstPage ? messages?.pagination?.a11yUnavailable?.() : messages?.pagination?.a11yPrevious?.({ page: previousPage })
                                }
                            >
                                <div className={bem('previousCopyAndArrow', disabledFirstPage, 'previousCopyAndArrow')}>
                                    <div className={bem('previousIcon', disabledFirstPage, className)} />{' '}
                                    <span className={bem('previousCopy', disabledFirstPage, className)}>{messages?.pagination?.previous?.()}</span>
                                </div>
                            </Link>
                        </div>
                    )}
                    <div className={bem('counter')}>
                        <span className={bem('currentPage', null, className)}>{messages?.pagination?.status?.({ currentPage, totalPages })}</span>
                    </div>

                    {!singlePage && (
                        <div className={bem('box')}>
                            <Link
                                aria-disabled={disabledLastPage ? 'true' : 'false'}
                                aria-label={disabledLastPage ? messages?.pagination?.a11yUnavailable?.() : messages?.pagination?.a11yNext?.({ page: nextPage })}
                                className={bem('next', disabledLastPage, className)}
                                onClick={() => this.onPaginateHandler('next')}
                                tabIndex={disabledLastPage ? '-1' : '0'}
                                to={nextPageLink}
                            >
                                <div className={bem('nextCopyAndArrow', disabledLastPage, 'nextCopyAndArrow')}>
                                    <div className={bem('nextCopy', disabledLastPage, className)}>{messages?.pagination?.next?.()}</div>
                                    <div className={bem('nextIcon', disabledLastPage, className)} />
                                </div>
                            </Link>
                            <Link
                                aria-disabled={disabledLastPage ? 'true' : 'false'}
                                aria-label={disabledLastPage ? messages?.pagination?.a11yUnavailable?.() : messages?.pagination?.a11yLast?.({ page: totalPages })}
                                className={bem('last', disabledLastPage, className)}
                                onClick={() => this.onPaginateHandler('last')}
                                tabIndex={disabledLastPage ? '-1' : '0'}
                                to={lastPageLink}
                            >
                                <div className={bem('lastIcon', disabledLastPage, className)} />
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

Pagination.propTypes = {
    className: string,
    currentPage: number.isRequired,
    totalPages: number.isRequired,
    location: shape({
        pathname: string.isRequired,
        search: string.isRequired,
    }),
    messages: shape({
        pagination: shape({
            previous: func.isRequired,
            status: func.isRequired,
            next: func.isRequired,
        }),
    }),
    onPaginate: func,
    generateListingUrl: func.isRequired,
    analytics: shape({
        category: shape({
            pageType: string.isRequired,
            subCategory1: string.isRequired,
        }),
    }),
    tracking: TrackingPropType,
    customPageSize: number,
    optimizely: object,
};

export default withOptimizely(withAnalytics(Pagination));
